import {
  sceneCoordsToViewportCoords,
  viewportCoordsToSceneCoords,
} from "@excalidraw/excalidraw";
import { createPortal } from "react-dom";

import type { ExcalidrawElement } from "@excalidraw/element/types";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { pivotScenePoint, scenePointToPivot } from "../animation/anchorPick";

/** Draggable crosshair over the canvas marking the active clip's rotation/
 * scale anchor. Reads scroll/zoom via the imperative API (useUIAppState
 * strips them; the parent re-renders on every onChange, so the handle stays
 * glued through pan/zoom); dragging streams pivot updates per frame and
 * commits one undo entry on release — the clip-timeline drag pattern. */
export const AnchorHandle = ({
  element,
  pivotX,
  pivotY,
  api,
  onChange,
}: {
  element: ExcalidrawElement;
  pivotX: number;
  pivotY: number;
  api: ExcalidrawImperativeAPI;
  onChange: (
    pivot: { pivotX: number; pivotY: number },
    commit: boolean,
  ) => void;
}) => {
  const scene = pivotScenePoint(element, pivotX, pivotY);
  const { x, y } = sceneCoordsToViewportCoords(
    { sceneX: scene.x, sceneY: scene.y },
    api.getAppState(),
  );

  const onPointerDown = (event: React.PointerEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const toPivot = (ev: PointerEvent) => {
      const p = viewportCoordsToSceneCoords(
        { clientX: ev.clientX, clientY: ev.clientY },
        api.getAppState(),
      );
      return scenePointToPivot(element, p.x, p.y);
    };
    const onMove = (ev: PointerEvent) => onChange(toPivot(ev), false);
    const onUp = (ev: PointerEvent) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointercancel", onUp);
      onChange(toPivot(ev), true);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, { once: true });
    window.addEventListener("pointercancel", onUp, { once: true });
  };

  return createPortal(
    <div
      title="Rotation anchor — drag to move"
      onPointerDown={onPointerDown}
      style={{
        position: "fixed",
        left: x - 10,
        top: y - 10,
        width: 20,
        height: 20,
        borderRadius: "50%",
        border: "2px solid #4dabf7",
        background: "rgba(77, 171, 247, 0.15)",
        boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.4)",
        cursor: "grab",
        zIndex: 40,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        touchAction: "none",
      }}
    >
      <div
        style={{
          width: 4,
          height: 4,
          borderRadius: "50%",
          background: "#4dabf7",
        }}
      />
    </div>,
    document.body,
  );
};
